/**
 * Participant attendance rows persisted on QuestionnaireResponse (`participant-attendance` group).
 */
import { z } from 'zod';
import { calendarStatusValues } from '@/schemas/appointment.schema';
import { locationRequiredForClientAttendance } from '@/lib/calendarClientAttendanceStatus';

export const participantAttendanceStatusValues = calendarStatusValues;

export type ParticipantAttendanceStatus = (typeof participantAttendanceStatusValues)[number];

export const PARTICIPANT_LOCATION_REQUIRED_STATUSES = new Set<ParticipantAttendanceStatus>(['attended']);

export const participantAttendanceEntrySchema = z
  .object({
    patientId: z.string().min(1),
    displayName: z.string().optional(),
    status: z.enum(participantAttendanceStatusValues),
    location: z.string().optional(),
  })
  .superRefine((entry, ctx) => {
    if (locationRequiredForClientAttendance(entry.status) && !entry.location?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Location is required for attended participants',
        path: ['location'],
      });
    }
  });

export type ParticipantAttendanceEntry = z.infer<typeof participantAttendanceEntrySchema>;

export const participantAttendanceListSchema = z
  .array(participantAttendanceEntrySchema)
  .min(1, 'At least one participant is required');

export function buildParticipantAttendanceEntries(
  rows: ReadonlyArray<{ patientId: string; displayName?: string }>,
  attendanceByPatientId: Record<string, ParticipantAttendanceStatus | string | undefined>,
  locationByPatientId: Record<string, string | undefined>,
): ParticipantAttendanceEntry[] {
  return rows.map((row) => ({
    patientId: row.patientId,
    displayName: row.displayName,
    status:
      (attendanceByPatientId[row.patientId] as ParticipantAttendanceStatus | undefined) ??
      'scheduled',
    location: locationByPatientId[row.patientId]?.trim() || undefined,
  }));
}

export function validateParticipantAttendance(
  rows: ReadonlyArray<{ patientId: string; displayName?: string }>,
  attendanceByPatientId: Record<string, ParticipantAttendanceStatus | string | undefined>,
  locationByPatientId: Record<string, string | undefined>,
):
  | { success: true; data: ParticipantAttendanceEntry[] }
  | { success: false; error: z.ZodError; locationErrorsByPatientId: Record<string, string> } {
  if (rows.length === 0) {
    return { success: true, data: [] };
  }

  const entries = buildParticipantAttendanceEntries(
    rows,
    attendanceByPatientId,
    locationByPatientId,
  );
  const parsed = participantAttendanceListSchema.safeParse(entries);
  if (parsed.success) {
    return parsed;
  }

  const locationErrorsByPatientId: Record<string, string> = {};
  for (const issue of parsed.error.issues) {
    if (issue.path.length >= 2 && issue.path[1] === 'location') {
      const index = issue.path[0];
      if (typeof index === 'number') {
        const row = entries[index];
        if (row) {
          locationErrorsByPatientId[row.patientId] = issue.message;
        }
      }
    }
  }

  return {
    success: false,
    error: parsed.error,
    locationErrorsByPatientId,
  };
}
